import React, { useState, useCallback, FormEvent } from 'react';
import { isValidEmail, isValidPassword } from '../utils/validation';
import './EmailSignupForm.css';

interface EmailSignupFormProps {
  onSubmit: (email: string, password: string, name?: string) => Promise<void>;
  onBackToMenu: () => void;
  onSwitchToLogin: () => void;
  isLoading?: boolean;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  terms?: string;
}

interface PasswordStrength {
  score: number; // 0-4
  label: string;
  color: string;
}

/**
 * EmailSignupForm Component
 * Form for creating a new account with email and password
 */
export const EmailSignupForm: React.FC<EmailSignupFormProps> = ({
  onSubmit,
  onBackToMenu,
  onSwitchToLogin,
  isLoading = false
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Calculate password strength
  const calculatePasswordStrength = useCallback((pwd: string): PasswordStrength => {
    if (!pwd) return { score: 0, label: '', color: '' };

    let score = 0;
    
    // Length check
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    
    // Character variety checks
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[@$!%*?&]/.test(pwd)) score++;

    // Cap at 4
    score = Math.min(4, score);

    const strengthMap: Record<number, { label: string; color: string }> = {
      0: { label: '', color: '' },
      1: { label: 'Weak', color: '#F44336' },
      2: { label: 'Fair', color: '#FF9800' },
      3: { label: 'Good', color: '#FFC107' },
      4: { label: 'Strong', color: '#4CAF50' }
    };

    return { score, ...strengthMap[score] };
  }, []);

  const passwordStrength = calculatePasswordStrength(password);

  // Validate individual fields
  const validateField = useCallback((field: string, value: string): string | undefined => {
    switch (field) {
      case 'email':
        if (!value) return 'Email is required';
        if (!isValidEmail(value)) return 'Please enter a valid email address';
        return undefined;
      
      case 'password':
        if (!value) return 'Password is required';
        if (!isValidPassword(value)) {
          return 'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
        }
        return undefined;
      
      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        if (value !== password) return 'Passwords do not match';
        return undefined;
      
      case 'terms':
        if (!termsAccepted) return 'You must accept the Terms & Conditions';
        return undefined;
      
      default:
        return undefined;
    }
  }, [password, termsAccepted]);

  // Handle field blur
  const handleBlur = useCallback((field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    const value = field === 'email' ? email : 
                  field === 'password' ? password :
                  field === 'confirmPassword' ? confirmPassword : '';
    
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  }, [email, password, confirmPassword, validateField]);

  // Validate all fields
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {
      email: validateField('email', email),
      password: validateField('password', password),
      confirmPassword: validateField('confirmPassword', confirmPassword),
      terms: validateField('terms', '')
    };

    setErrors(newErrors);
    setTouched({
      email: true,
      password: true,
      confirmPassword: true,
      terms: true
    });

    return !Object.values(newErrors).some(error => error !== undefined);
  }, [email, password, confirmPassword, validateField]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(email, password, name || undefined);
    } catch (error) {
      console.error('Signup failed:', error);
    }
  }, [email, password, name, validateForm, onSubmit]);

  return (
    <div className="email-signup-form">
      <button
        className="email-signup-back"
        onClick={onBackToMenu}
        type="button"
        disabled={isLoading}
      >
        ← Back to Menu
      </button>

      <h2 className="email-signup-title">Create Your Account</h2>

      <form onSubmit={handleSubmit} noValidate>
        {/* Name Field (Optional) */}
        <div className="email-signup-field">
          <label htmlFor="signup-name" className="email-signup-label">
            Name (Optional)
          </label>
          <input
            id="signup-name"
            type="text"
            className="email-signup-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name"
            disabled={isLoading}
            autoComplete="name"
          />
        </div>

        {/* Email Field */}
        <div className="email-signup-field">
          <label htmlFor="signup-email" className="email-signup-label">
            Email Address *
          </label>
          <input
            id="signup-email"
            type="email"
            className={`email-signup-input ${
              touched.email && errors.email ? 'email-signup-input-error' : ''
            } ${
              touched.email && !errors.email && email ? 'email-signup-input-success' : ''
            }`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => handleBlur('email')}
            placeholder="your.email@example.com"
            disabled={isLoading}
            required
            autoComplete="email"
          />
          {touched.email && !errors.email && email && (
            <span className="email-signup-success">✓ Valid email format</span>
          )}
          {touched.email && errors.email && (
            <span className="email-signup-error">{errors.email}</span>
          )}
        </div>

        {/* Password Field */}
        <div className="email-signup-field">
          <label htmlFor="signup-password" className="email-signup-label">
            Password *
          </label>
          <div className="email-signup-input-wrapper">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              className={`email-signup-input ${
                touched.password && errors.password ? 'email-signup-input-error' : ''
              }`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => handleBlur('password')}
              placeholder="Enter password"
              disabled={isLoading}
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              className="email-signup-toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              disabled={isLoading}
            >
              {showPassword ? '👁' : '👁‍🗨'}
            </button>
          </div>
          
          {/* Password Strength Indicator */}
          {password && (
            <div className="email-signup-password-strength">
              <div className="email-signup-password-strength-bar">
                <div
                  className="email-signup-password-strength-fill"
                  style={{
                    width: `${(passwordStrength.score / 4) * 100}%`,
                    backgroundColor: passwordStrength.color
                  }}
                />
              </div>
              <span
                className="email-signup-password-strength-label"
                style={{ color: passwordStrength.color }}
              >
                Password Strength: {passwordStrength.label}
              </span>
            </div>
          )}

          {/* Password Requirements */}
          <div className="email-signup-password-requirements">
            <div className={password.length >= 8 ? 'requirement-met' : ''}>
              • At least 8 characters
            </div>
            <div className={/[A-Z]/.test(password) && /[a-z]/.test(password) ? 'requirement-met' : ''}>
              • Uppercase & lowercase letters
            </div>
            <div className={/\d/.test(password) && /[@$!%*?&]/.test(password) ? 'requirement-met' : ''}>
              • Number & special character
            </div>
          </div>

          {touched.password && errors.password && (
            <span className="email-signup-error">{errors.password}</span>
          )}
        </div>

        {/* Confirm Password Field */}
        <div className="email-signup-field">
          <label htmlFor="signup-confirm-password" className="email-signup-label">
            Confirm Password *
          </label>
          <div className="email-signup-input-wrapper">
            <input
              id="signup-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              className={`email-signup-input ${
                touched.confirmPassword && errors.confirmPassword ? 'email-signup-input-error' : ''
              } ${
                touched.confirmPassword && !errors.confirmPassword && confirmPassword ? 'email-signup-input-success' : ''
              }`}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => handleBlur('confirmPassword')}
              placeholder="Confirm password"
              disabled={isLoading}
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              className="email-signup-toggle-password"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              disabled={isLoading}
            >
              {showConfirmPassword ? '👁' : '👁‍🗨'}
            </button>
          </div>
          {touched.confirmPassword && !errors.confirmPassword && confirmPassword && (
            <span className="email-signup-success">✓ Passwords match</span>
          )}
          {touched.confirmPassword && errors.confirmPassword && (
            <span className="email-signup-error">{errors.confirmPassword}</span>
          )}
        </div>

        {/* Terms & Conditions */}
        <div className="email-signup-field email-signup-checkbox-field">
          <label className="email-signup-checkbox-label">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              disabled={isLoading}
              required
            />
            <span>
              I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
            </span>
          </label>
          {touched.terms && errors.terms && (
            <span className="email-signup-error">{errors.terms}</span>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="email-signup-submit"
          disabled={isLoading}
        >
          {isLoading ? '⟳ Creating Account...' : 'Create Account'}
        </button>

        {/* Switch to Login */}
        <div className="email-signup-footer">
          Already have an account?{' '}
          <button
            type="button"
            className="email-signup-link"
            onClick={onSwitchToLogin}
            disabled={isLoading}
          >
            Login
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailSignupForm;
